import { useCallback } from 'react'
import type { ReactFlowInstance, Node } from '@xyflow/react'
import { createTextNode, createImageNode, createVideoNode, createTextFileNode, createImageFileNode, createCommentNode, createMaskNode, createDataTableNode, createVariationGeneratorNode, createPromptComposerNode, createBatchGeneratorNode } from '@/lib/canvas'
import { createAudioFileNode, createNode3D, createShaderNode, createVideoFileNode } from '@/lib/canvas/utils/nodeFactory'
import { requestShaderComposerFocus } from '@/components/canvas/shader/composer-focus'
import { requestCloseShaderLibrary } from '@/components/canvas/shader/library-focus'
import { FEATURES } from '@/lib/features'
import { getSubPurposeLabel } from '@genieartinc/schemas'
import { ensureInpaintHandles } from '@genieartinc/schemas/pools/ai'
import { toReactFlowNode } from '@/lib/canvas/to-react-flow-node'
import type { CanvasTool } from '@/types/canvas'
import { usePostHog } from 'posthog-js/react'

interface UseCanvasToolSelectParams {
  canvasRef: React.RefObject<ReactFlowInstance | null>
  canvasId: string | undefined
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>
  setActiveTool: React.Dispatch<React.SetStateAction<CanvasTool>>
  onOpenTrainDialog?: () => void
  onOpenLibrary?: (options?: { tab?: string }) => void
}

/** Where the tool came from: a toolbar click, the cursor (keyboard shortcut), or a catalogue drag dropped on the canvas. */
type ToolOrigin = 'toolbar' | 'cursor' | 'drop'

/**
 * Encapsulates toolbar → node-creation logic.
 *
 * Maps tool identifiers to node factory functions and adds the resulting
 * React Flow node to the canvas at the mouse cursor position (when provided)
 * or the current viewport centre as fallback.
 */
export function useCanvasToolSelect({
  canvasRef,
  canvasId,
  setNodes,
  setActiveTool,
  onOpenTrainDialog,
  onOpenLibrary,
}: UseCanvasToolSelectParams) {
  const posthog = usePostHog()
  const handleToolSelect = useCallback((
    tool: string,
    screenPos?: { x: number; y: number },
    origin: ToolOrigin = screenPos ? 'cursor' : 'toolbar',
  ) => {
    if (!tool) {
      setActiveTool(undefined)
      return
    }

    posthog.capture('canvas_tool_selected', {
      tool,
      canvas_id: canvasId,
      via_keyboard: origin === 'cursor',
      origin,
    })

    // Use provided screen position (keyboard shortcut) or fall back to viewport center (toolbar click)
    const pos = screenPos ?? { x: window.innerWidth / 2, y: window.innerHeight / 2 }
    const viewportCenter = canvasRef.current
      ? canvasRef.current.screenToFlowPosition(pos)
      : { x: 0, y: 0 }

    // Tool-to-node creation mapping
    const toolCreators: Record<string, () => ReturnType<typeof createTextNode> | null> = {
      'text': () => createTextNode(viewportCenter),
      'text-file': () => createTextFileNode(viewportCenter),
      'data-table': () => createDataTableNode(viewportCenter),
      'variation-generator': () => createVariationGeneratorNode(viewportCenter),
      'prompt-composer': () => createPromptComposerNode(viewportCenter),
      'batch-generator': () => createBatchGeneratorNode(viewportCenter),
      'image-create': () => createImageNode(viewportCenter, 'create'),
      'image-edit': () => createImageNode(viewportCenter, 'edit'),
      'image-enhance': () => createImageNode(viewportCenter, 'enhance'),
      'image-file': () => createImageFileNode(viewportCenter),
      'video-create': () => createVideoNode(viewportCenter, 'create'),
      'video-file': () => createVideoFileNode(viewportCenter),
      'audio-file': () => createAudioFileNode(viewportCenter),
      'video-edit': () => createVideoNode(viewportCenter, 'edit'),
      'video-enhance': () => createVideoNode(viewportCenter, 'enhance'),
      // No toolbar, catalogue, palette or shortcut can emit '3d-create' while NODE_3D is
      // off (see lib/features.ts); kept behind the same flag rather than left reachable.
      ...(FEATURES.NODE_3D ? { '3d-create': () => createNode3D(viewportCenter, 'create') } : {}),
      'shader': () => createShaderNode(viewportCenter),
      'shader-ai': () => createShaderNode(viewportCenter),
    }

    // 'shader-library' → open the Library modal pinned to shaders tab
    if (tool === 'shader-library') {
      onOpenLibrary?.({ tab: 'shaders' })
      setActiveTool(undefined)
      return
    }

    // Comment tool enters placement mode — node created on pane click.
    // A drop already picked the spot, so it places the node there instead.
    if (tool === 'comment') {
      if (origin !== 'drop') {
        setActiveTool('comment')
        return
      }
      const node = createCommentNode(viewportCenter)
      if (node) {
        if (canvasId && node.component?.state) node.component.state.canvasId = canvasId
        setNodes((prev) => [...prev.map(existing => existing.selected ? { ...existing, selected: false } : existing), { ...toReactFlowNode(node), selected: true }])
      }
      return
    }

    // Mask gets its own custom node (not BaseNode/ImageNode)
    if (tool === 'image-tools-mask') {
      const node = createMaskNode(viewportCenter)
      if (node) {
        if (canvasId && node.component?.state) node.component.state.canvasId = canvasId
        setNodes((prev) => [...prev.map(existing => existing.selected ? { ...existing, selected: false } : existing), { ...toReactFlowNode(node), selected: true }])
        setActiveTool(undefined)
      }
      return
    }

    // Training tools open the training dialog instead of creating a node
    if (tool === 'image-tools-training' || tool === 'video-tools-training') {
      onOpenTrainDialog?.()
      setActiveTool(undefined)
      return
    }

    // Handle tool subPurpose selections from the toolbar (e.g., "image-tools-inpaint")
    if (tool.startsWith('image-tools-')) {
      const subPurpose = tool.replace('image-tools-', '')
      const node = createImageNode(viewportCenter, 'tools')
      if (node) {
        if (node.component?.state) {
          node.component.state.subPurpose = subPurpose
          node.component.state.title = getSubPurposeLabel(subPurpose)
          if (canvasId) node.component.state.canvasId = canvasId
        }
        if (subPurpose === 'inpaint') node.handles = ensureInpaintHandles(node.handles, 'inpaint')
        setNodes((prev) => [...prev.map(existing => existing.selected ? { ...existing, selected: false } : existing), { ...toReactFlowNode(node), selected: true }])
        setActiveTool(undefined)
      }
      return
    }

    // Handle video tool subPurpose selections (e.g., "video-tools-lipsync")
    if (tool.startsWith('video-tools-')) {
      const subPurpose = tool.replace('video-tools-', '')
      const node = createVideoNode(viewportCenter, 'tools')
      if (node) {
        if (node.component?.state) {
          node.component.state.subPurpose = subPurpose
          node.component.state.title = getSubPurposeLabel(subPurpose)
          if (canvasId) node.component.state.canvasId = canvasId
        }
        setNodes((prev) => [...prev.map(existing => existing.selected ? { ...existing, selected: false } : existing), { ...toReactFlowNode(node), selected: true }])
        setActiveTool(undefined)
      }
      return
    }

    const creator = toolCreators[tool]
    if (creator) {
      const node = creator()
      if (node) {
        if (canvasId && node.component?.state) {
          node.component.state.canvasId = canvasId
        }
        const flowNode = toReactFlowNode(node)
        // Create shader lands in the prompt. The request is held until the composer mounts.
        if (tool === 'shader' || tool === 'shader-ai') {
          requestShaderComposerFocus(flowNode.id)
          requestCloseShaderLibrary()
        }
        setNodes((prev) => [...prev.map(existing => existing.selected ? { ...existing, selected: false } : existing), { ...flowNode, selected: true }])
        setActiveTool(undefined)
      }
    } else {
      setActiveTool(tool as CanvasTool)
    }
  }, [canvasRef, canvasId, setNodes, setActiveTool, posthog, onOpenTrainDialog, onOpenLibrary])

  /** A tool dropped on the canvas: same creation path, placed where the drag ended. */
  const handleToolDrop = useCallback(
    (tool: string, screenPos: { x: number; y: number }) => handleToolSelect(tool, screenPos, 'drop'),
    [handleToolSelect],
  )

  return { handleToolSelect, handleToolDrop }
}
